import axios from "axios";

// Isolated client for the Estimating module. Same token/401 behavior as the main app client.
const client = axios.create({ baseURL: `${window.location.origin}/api/estimating` });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const unwrap = (p) => p.then((r) => r.data);

// Operations lives on its own prefix but shares the same token behaviour.
const opsClient = axios.create({ baseURL: `${window.location.origin}/api/operations` });
opsClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const opsApi = {
  getScopeHours: (estimateId) => unwrap(opsClient.get(`/estimates/${estimateId}/scope-hours`)),
  publishScopeHours: (estimateId, scopeCodes = []) =>
    unwrap(opsClient.post(`/estimates/${estimateId}/scope-hours/publish`, { scope_codes: scopeCodes })),
  getProjectScopes: (params) => unwrap(opsClient.get("/project-scopes", { params })),
  getSettings: () => unwrap(opsClient.get("/settings")),
  saveSettings: (body) => unwrap(opsClient.put("/settings", body)),
  listScopes: () => unwrap(opsClient.get("/scopes")),
  createScope: (body) => unwrap(opsClient.post("/scopes", body)),
};

function upload(path, file, fields = {}) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  return unwrap(client.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } }));
}

// --- Pre-Con bid intake (PRE-1) ---------------------------------------------------------------
const preconClient = axios.create({ baseURL: `${window.location.origin}/api/estimating/precon` });
preconClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const preconApi = {
  listCards: (params) => unwrap(preconClient.get("/itb", { params })),
  getCard: (id) => unwrap(preconClient.get(`/itb/${id}`)),
  editCard: (id, parsed) => unwrap(preconClient.put(`/itb/${id}`, { parsed })),
  intakeManual: (payload) => unwrap(preconClient.post("/intake/manual", payload)),
  reclassify: (id, type) => unwrap(preconClient.post(`/itb/${id}/reclassify`, { type })),
  bid: (id) => unwrap(preconClient.post(`/itb/${id}/bid`)),
  answerSameJob: (id, jobId, same) =>
    unwrap(preconClient.post(`/itb/${id}/same-job`, { job_id: jobId, same })),
  undoMerge: (id) => unwrap(preconClient.post(`/itb/${id}/undo-merge`)),
  matchToJob: (id, jobId) => unwrap(preconClient.post(`/itb/${id}/match`, { job_id: jobId })),
  decline: (id, payload) => unwrap(preconClient.post(`/itb/${id}/decline`, payload)),
  later: (id, until) => unwrap(preconClient.post(`/itb/${id}/later`, { until })),
  wake: (id) => unwrap(preconClient.post(`/itb/${id}/wake`)),
  getSettings: () => unwrap(preconClient.get("/settings")),
  saveSettings: (data) => unwrap(preconClient.put("/settings", { data })),
  getDigest: () => unwrap(preconClient.get("/digest")),
  getEvents: (params) => unwrap(preconClient.get("/events", { params })),
  listDocuments: (jobId, params) => unwrap(preconClient.get(`/jobs/${jobId}/documents`, { params })),
  uploadDocument: (jobId, file, folder = "bid_documents", onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    return unwrap(preconClient.post(`/jobs/${jobId}/documents`, fd,
      { headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) }));
  },
  downloadDocument: (docId) => preconClient.get(`/documents/${docId}/download`, { responseType: "blob" }).then((r) => r.data),
  documentUrl: (docId) =>
    `${window.location.origin}/api/estimating/precon/documents/${docId}/download`,

  // PRE-2 — the board
  listJobs: (params) => unwrap(preconClient.get("/jobs", { params })),
  getJob: (id) => unwrap(preconClient.get(`/jobs/${id}`)),
  createJob: (body) => unwrap(preconClient.post("/jobs", body)),
  patchJob: (id, data) => unwrap(preconClient.patch(`/jobs/${id}`, { data })),
  setJobStatus: (id, status, reason = "") =>
    unwrap(preconClient.post(`/jobs/${id}/status`, { status, reason })),
  setInvitationStatus: (id, invId, status, reason = "") =>
    unwrap(preconClient.post(`/jobs/${id}/invitations/${invId}/status`, { status, reason })),
  addInvitation: (id, data) => unwrap(preconClient.post(`/jobs/${id}/invitations`, data)),
  setInvitationContact: (id, invId, contactId) => unwrap(preconClient.post(`/jobs/${id}/invitations/${invId}/contact`, { contact_id: contactId || "" })),
  dropInvitation: (id, invId) => unwrap(preconClient.delete(`/jobs/${id}/invitations/${invId}`)),
  assignable: () => unwrap(preconClient.get("/assignable")),
  assignmentProposal: (id) => unwrap(preconClient.get(`/jobs/${id}/assignment-proposal`)),
  assign: (id, body) => unwrap(preconClient.post(`/jobs/${id}/assign`, body)),
  addNote: (id, text, pinned = false) =>
    unwrap(preconClient.post(`/jobs/${id}/notes`, { text, pinned })),
  pinNote: (id, noteId, pinned) =>
    unwrap(preconClient.post(`/jobs/${id}/notes/${noteId}/pin`, null, { params: { pinned } })),
  calendar: (params) => unwrap(preconClient.get("/board/calendar", { params })),
  boardDigest: () => unwrap(preconClient.get("/board/digest")),
  runReplay: () => unwrap(preconClient.post("/match/replay")),
  lastReplay: () => unwrap(preconClient.get("/match/replay")),
  notifications: () => unwrap(preconClient.get("/notifications")),

  // PRE-3 — Will Bid -> Estimate
  getEstimateBanner: (estimateId) => unwrap(preconClient.get(`/estimates/${estimateId}/banner`)),
  acknowledgeAddendum: (cardId) => unwrap(preconClient.post(`/cards/${cardId}/acknowledge`)),
  jobEstimate: (jobId) => unwrap(preconClient.get(`/jobs/${jobId}/estimate`)),
  markNotificationsRead: () => unwrap(preconClient.post("/notifications/read")),
  readNotificationTarget: (target) => unwrap(preconClient.post("/notifications/read-target", target)),

  // BID-1 — statuses, stages, No Bid, saved views, columns
  setBidStatus: (id, body) => unwrap(preconClient.post(`/jobs/${id}/bid-status`, body)),
  setBoardStatus: (id, body) => unwrap(preconClient.post(`/jobs/${id}/board-status`, body)),
  getAssignmentProfiles: () => unwrap(preconClient.get("/board/assignment-profiles")),
  putAssignmentProfiles: (profiles) => unwrap(preconClient.put("/board/assignment-profiles", { profiles })),
  overrideStage: (id, body) => unwrap(preconClient.post(`/jobs/${id}/stage`, body)),
  reconcileComplete: (id, checklist) => unwrap(preconClient.post(`/jobs/${id}/reconcile-complete`, checklist)),
  // BID MASTER — mapping table, workspace helpers
  statusMapping: () => unwrap(preconClient.get("/board/status-mapping")),
  putStatusMapping: (body) => unwrap(preconClient.put("/board/status-mapping", body)),
  jobByEstimate: (estimateId) => unwrap(preconClient.get(`/jobs/by-estimate/${estimateId}`)),
  switcher: () => unwrap(preconClient.get("/board/switcher")),
  jobFromEstimate: (estimateId) => unwrap(preconClient.post("/jobs/from-estimate", { estimate_id: estimateId })),
  migrateReparent: (dryRun = true) => unwrap(preconClient.post("/board/migrate/reparent", null, { params: { dry_run: dryRun } })),
  transitions: () => unwrap(preconClient.get("/board/transitions")),
  saveStageSettings: (body) => unwrap(preconClient.put("/board/stage-settings", body)),
  listFilters: () => unwrap(preconClient.get("/board/filters")),
  saveFilter: (body) => unwrap(preconClient.post("/board/filters", body)),
  setDefaultFilter: (fid, on = true) => unwrap(preconClient.post(`/board/filters/${fid}/default`, null, { params: { on } })),
  deleteFilter: (fid) => unwrap(preconClient.delete(`/board/filters/${fid}`)),
  getColumns: () => unwrap(preconClient.get("/board/columns")),
  putColumns: (body) => unwrap(preconClient.put("/board/columns", body)),
  // BOD-1
  bodOptions: (id) => unwrap(preconClient.get(`/jobs/${id}/bod-options`)),
  setBod: (id, body) => unwrap(preconClient.post(`/jobs/${id}/bod`, body)),
  // BID-2
  calendar2: (params) => unwrap(preconClient.get("/board/calendar2", { params })),
  overload: (id) => unwrap(preconClient.get(`/jobs/${id}/overload`)),
  overloadDecision: (id, body) => unwrap(preconClient.post(`/jobs/${id}/overload-decision`, body)),
  jobEmails: (id) => unwrap(preconClient.get(`/jobs/${id}/emails`)),
  linkCardToBid: (cardId, jobId) => unwrap(preconClient.post(`/cards/${cardId}/link-bid`, null, { params: { job_id: jobId } })),
  mentionUsers: (q) => unwrap(preconClient.get("/mention-users", { params: { q } })),
  alerts: () => unwrap(preconClient.get("/alerts")),
  saveBid2Settings: (body) => unwrap(preconClient.put("/board/bid2-settings", body)),
};

export const estApi = {
  // Reference data
  getCostTypes: () => unwrap(client.get("/cost-types")),
  getCostCodes: (activeOnly = true) => unwrap(client.get("/cost-codes", { params: { active_only: activeOnly } })),
  getSettings: () => unwrap(client.get("/settings")),
  getCostCodes: () => unwrap(client.get("/cost-codes")),
  getSchema: () => unwrap(client.get("/schema")),

  // Phase 1.2 imports
  previewCostCodes: (file) => upload("/import/cost-codes/preview", file),
  commitCostCodes: (previewId) => unwrap(client.post("/import/cost-codes/commit", { preview_id: previewId })),
  previewCostTypes: (file) => upload("/import/cost-types/preview", file),
  commitCostTypes: (previewId) => unwrap(client.post("/import/cost-types/commit", { preview_id: previewId })),

  // Budget code matrix
  getMatrix: () => unwrap(client.get("/budget-codes/matrix")),
  saveMatrix: (updates) => unwrap(client.put("/budget-codes/matrix", { updates })),
  regenerateBudgetCodes: () => unwrap(client.post("/budget-codes/regenerate")),
  createCostCode: (body) => unwrap(client.post("/cost-codes", body)),
  updateCostCode: (id, body) => unwrap(client.patch(`/cost-codes/${id}`, body)),
  createCostType: (body) => unwrap(client.post("/cost-types", body)),
  updateCostType: (id, body) => unwrap(client.patch(`/cost-types/${id}`, body)),

  // Phase 1.3 price list import
  getPriceListMapping: () => unwrap(client.get("/import/price-list/mapping")),
  savePriceListMapping: (mapping) => unwrap(client.put("/import/price-list/mapping", { mapping })),
  previewPriceList: (file, mapping, skipStale) =>
    upload("/import/price-list/preview", file, {
      mapping_json: JSON.stringify(mapping || {}),
      skip_stale: skipStale ? "true" : "false",
    }),
  commitPriceList: (previewId, overwriteEdited) =>
    unwrap(client.post("/import/price-list/commit", {
      preview_id: previewId, overwrite_edited: !!overwriteEdited,
    })),

  // Price update template + price-only re-import
  downloadPriceTemplate: async (params, fmt = "xlsx") => {
    const res = await client.get("/price-list/import-template", {
      params: { ...params, fmt }, responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ABS-price-update-${new Date().toISOString().slice(0, 10)}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  },
  previewPriceUpdate: (file, mode, createNew) =>
    upload("/price-list/import/preview", file, {
      mode, create_new: createNew ? "true" : "false",
    }),
  commitPriceUpdate: (stagingId) =>
    unwrap(client.post("/price-list/import/commit", { staging_id: stagingId })),
  getPriceImportBatches: () => unwrap(client.get("/price-list/import/batches")),
  undoPriceImport: (batchId) => unwrap(client.post(`/price-list/import/${batchId}/undo`)),

  // Price list read
  getPriceList: (params) => unwrap(client.get("/price-list", { params })),
  getPriceListStats: () => unwrap(client.get("/price-list/stats")),
  getFacets: () => unwrap(client.get("/price-list/facets")),
  getCodeOptions: () => unwrap(client.get("/price-list/options")),
  exportCsv: async (params) => {
    const res = await client.get("/price-list/export", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `price-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Item CRUD (Phase 2.1)
  createItem: (data) => unwrap(client.post("/price-list", { data })),
  updateItem: (id, data) => unwrap(client.put(`/price-list/${id}`, { data })),
  duplicateItem: (id) => unwrap(client.post(`/price-list/${id}/duplicate`)),
  setItemActive: (id, active) => unwrap(client.patch(`/price-list/${id}/active`, { active })),
  deleteItem: (id) => unwrap(client.delete(`/price-list/${id}`)),
  getItemHistory: (id) => unwrap(client.get(`/price-list/${id}/history`)),
  bulkEdit: (payload) => unwrap(client.post("/price-list/bulk-edit", payload)),

  // Groups (Phase 2.2)
  getGroups: (params) => unwrap(client.get("/groups", { params })),
  getGroup: (id) => unwrap(client.get(`/groups/${id}`)),
  createGroup: (data) => unwrap(client.post("/groups", { data })),
  updateGroup: (id, data) => unwrap(client.put(`/groups/${id}`, { data })),
  newGroupVersion: (id, data) => unwrap(client.post(`/groups/${id}/version`, { data })),
  setGroupActive: (id, active) => unwrap(client.patch(`/groups/${id}/active`, { active })),
  deleteGroup: (id) => unwrap(client.delete(`/groups/${id}`)),
  addItemsToGroup: (id, itemIds, qty = 1) =>
    unwrap(client.post(`/groups/${id}/add-items`, { item_ids: itemIds, qty_multiplier: qty })),

  getImportLogs: () => unwrap(client.get("/import/logs")),

  // Estimates / Proposals (Phase 3)
  getEstimates: (params) => unwrap(client.get("/estimates", { params })),
  getEstimateFilters: () => unwrap(client.get("/estimates/filters")),
  getEstimate: (id) => unwrap(client.get(`/estimates/${id}`)),
  createEstimate: (data) => unwrap(client.post("/estimates", { data })),
  updateEstimate: (id, data) => unwrap(client.put(`/estimates/${id}`, { data })),
  setEstimateStatus: (id, status) => unwrap(client.patch(`/estimates/${id}/status`, { status })),
  deleteEstimate: (id) => unwrap(client.delete(`/estimates/${id}`)),

  getPrefs: () => unwrap(client.get("/prefs")),
  savePrefs: (patch) => unwrap(client.put("/prefs", patch)),

  // Internal notes (append-only, stamped with author + timestamp)
  getInternalNotes: (id) => unwrap(client.get(`/estimates/${id}/internal-notes`)),
  addInternalNote: (id, text) => unwrap(client.post(`/estimates/${id}/internal-notes`, { text })),
  updateInternalNote: (id, noteId, text) =>
    unwrap(client.put(`/estimates/${id}/internal-notes/${noteId}`, { text })),
  deleteInternalNote: (id, noteId) =>
    unwrap(client.delete(`/estimates/${id}/internal-notes/${noteId}`)),
  getStatuses: () => unwrap(client.get("/statuses")),

  // Companies / contacts / people (Phase 4)
  getCompanies: (params) => unwrap(client.get("/companies", { params })),
  // Customers & contacts — resolve / ledger / profile / merge
  resolveParty: (body) => unwrap(client.post("/directory/resolve", body)),
  acceptParty: (body) => unwrap(client.post("/directory/resolve/accept", body)),
  companyProfile: (id) => unwrap(client.get(`/directory/companies/${id}/profile`)),
  directoryDuplicates: () => unwrap(client.get("/directory/duplicates")),
  mergeCompanies: (keepId, mergeId) => unwrap(client.post("/directory/merge", { keep_id: keepId, merge_id: mergeId })),
  rebuildPartyLinks: () => unwrap(client.post("/directory/party-links/rebuild")),
  createCompany: (data) => unwrap(client.post("/companies", { data })),
  addCompanyNote: (id, text) => unwrap(client.post(`/companies/${id}/notes`, { data: { text } })),
  updateCompany: (id, data) => unwrap(client.put(`/companies/${id}`, { data })),
  deleteCompany: (id) => unwrap(client.delete(`/companies/${id}`)),
  getContacts: (params) => unwrap(client.get("/contacts", {
    params, paramsSerializer: { indexes: null },
  })),
  createContact: (data) => unwrap(client.post("/contacts", { data })),
  updateContact: (id, data) => unwrap(client.put(`/contacts/${id}`, { data })),
  deleteContact: (id) => unwrap(client.delete(`/contacts/${id}`)),

  // Vendor quote uploads — store the PDF, read it, hand back a draft
  uploadQuoteFile: (estimateId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return unwrap(client.post(`/estimates/${estimateId}/quotes/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
    }));
  },
  getQuoteFiles: (estimateId) => unwrap(client.get(`/estimates/${estimateId}/quote-files`)),
  deleteQuoteFile: (fileId) => unwrap(client.delete(`/quote-files/${fileId}`)),

  // Master directory (Settings → Directory)
  directorySummary: () => unwrap(client.get("/directory/summary")),
  directoryUnlinked: () => unwrap(client.get("/directory/unlinked")),
  directoryLink: (estimate_id, company_id) =>
    unwrap(client.post("/directory/link", { estimate_id, company_id })),
  directorySalesNames: (limit = 400) =>
    unwrap(client.get("/directory/sales-names", { params: { limit } })),
  directoryImportNames: (names, company_type = "gc") =>
    unwrap(client.post("/directory/import-sales-names", { names, company_type })),
  getDueSummary: () => unwrap(client.get("/estimates/due-summary")),
  getPeople: () => unwrap(client.get("/people")),

  // Estimate lines / summary / quotes (Phase 5.1 + 5.2)
  getLines: (id) => unwrap(client.get(`/estimates/${id}/lines`)),
  addLines: (id, body) => unwrap(client.post(`/estimates/${id}/lines`, body)),
  updateLine: (lineId, data) => unwrap(client.put(`/lines/${lineId}`, { data })),
  duplicateLine: (lineId) => unwrap(client.post(`/lines/${lineId}/duplicate`)),
  deleteLine: (lineId) => unwrap(client.delete(`/lines/${lineId}`)),
  bulkLines: (payload) => unwrap(client.post("/lines/bulk", payload)),
  reorderLines: (id, orderedIds) =>
    unwrap(client.post(`/estimates/${id}/lines/reorder`, { ordered_ids: orderedIds })),
  applyGm: (id, payload) => unwrap(client.post(`/estimates/${id}/apply-gm`, payload)),
  saveDefaults: (id, data) => unwrap(client.put(`/estimates/${id}/defaults`, { data })),
  addMisc: (id, data) => unwrap(client.post(`/estimates/${id}/misc`, { data })),
  updateMisc: (id, miscId, data) => unwrap(client.put(`/estimates/${id}/misc/${miscId}`, { data })),
  deleteMisc: (id, miscId) => unwrap(client.delete(`/estimates/${id}/misc/${miscId}`)),
  getQuotes: (id) => unwrap(client.get(`/estimates/${id}/quotes`)),
  addQuote: (id, body) => unwrap(client.post(`/estimates/${id}/quotes`, body)),
  deleteQuote: (id, quoteId) => unwrap(client.delete(`/estimates/${id}/quotes/${quoteId}`)),
  setQuotePriceMode: (id, quoteId, mode) =>
    unwrap(client.post(`/estimates/${id}/quotes/${quoteId}/price-mode`, { mode })),

  // Qualifications (Phase 5.3)
  getQualLibrary: () => unwrap(client.get("/qualification-library")),
  createQualEntry: (data) => unwrap(client.post("/qualification-library", { data })),
  updateQualEntry: (entryId, data) => unwrap(client.put(`/qualification-library/${entryId}`, { data })),
  deleteQualEntry: (entryId) => unwrap(client.delete(`/qualification-library/${entryId}`)),
  saveScopeTags: (payload) => unwrap(client.put("/qualification-scope-settings", payload)),
  getQualifications: (id) => unwrap(client.get(`/estimates/${id}/qualifications`)),
  syncQualifications: (id) => unwrap(client.post(`/estimates/${id}/qualifications/sync`)),
  saveQualifications: (id, qualifications) =>
    unwrap(client.put(`/estimates/${id}/qualifications`, { qualifications })),
  resetQualifications: (id) => unwrap(client.post(`/estimates/${id}/qualifications/reset`)),
  // EST-QUAL-OT — the optional schedule-acceleration qualification (math is server-side + locked)
  getAcceleration: (id) => unwrap(client.get(`/estimates/${id}/acceleration`)),
  saveAcceleration: (id, included) =>
    unwrap(client.put(`/estimates/${id}/acceleration`, { included })),
  // Win → project: which scopes were actually awarded
  winPreview: (id) => unwrap(client.get(`/estimates/${id}/win-preview`)),
  win: (id, body) => unwrap(client.post(`/estimates/${id}/win`, body)),

  // Proposal (Phase 6)
  getProposal: (id) => unwrap(client.get(`/estimates/${id}/proposal`)),
  saveProposal: (id, data) => unwrap(client.put(`/estimates/${id}/proposal`, { data })),
  createProposalVersion: (id) => unwrap(client.post(`/estimates/${id}/proposal/versions`)),
  downloadProposalPdf: async (id, version, fileName) => {
    const res = await client.get(`/estimates/${id}/proposal/pdf`, {
      params: version ? { version } : {}, responseType: "blob",
    });
    const server = /filename="?([^";]+)"?/.exec(res.headers?.["content-disposition"] || "");
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || (server && server[1]) || "ABS Proposal.pdf";
    a.click();
    URL.revokeObjectURL(url);
  },
  getEmailDraft: (id, companyId = "") => unwrap(client.get(`/estimates/${id}/proposal/email-draft`, { params: companyId ? { company_id: companyId } : {} })),
  sendProposal: (id, payload) => unwrap(client.post(`/estimates/${id}/proposal/email`, payload)),
  getProposalEmailLog: (id) => unwrap(client.get(`/estimates/${id}/proposal/email-log`)),
  markProposalSent: (id) => unwrap(client.post(`/estimates/${id}/proposal/mark-sent`)),
  saveSettings: (data) => unwrap(client.put("/settings", { data })),

  // ITB inbox (Phase 8)
  listItb: (params) => unwrap(client.get("/itb", { params })),
  itbIntake: (payload) => unwrap(client.post("/itb/intake", payload)),
  acceptItb: (itbId) => unwrap(client.post(`/itb/${itbId}/accept`)),
  discardItb: (itbId) => unwrap(client.post(`/itb/${itbId}/discard`)),
  mergeItb: (itbId, targetId) => unwrap(client.post(`/itb/${itbId}/merge`, { target_id: targetId })),
  createEstimateFromItb: (itbId) => unwrap(client.post(`/itb/${itbId}/create-estimate`)),
  setItbSettings: (payload) => unwrap(client.put("/itb-settings", payload)),

  // Takeoff / FRS import (Phase 9)
  takeoffPreview: (id, form) => unwrap(client.post(`/estimates/${id}/takeoff/preview`, form,
    { headers: { "Content-Type": "multipart/form-data" } })),
  takeoffCommit: (id, payload) => unwrap(client.post(`/estimates/${id}/takeoff/commit`, payload)),
  getTakeoffBatches: (id) => unwrap(client.get(`/estimates/${id}/takeoff/batches`)),
  undoTakeoffBatch: (id, batchId) =>
    unwrap(client.delete(`/estimates/${id}/takeoff/batches/${batchId}`)),
  getTakeoffAliases: () => unwrap(client.get("/takeoff/aliases")),
  saveTakeoffAliases: (payload) => unwrap(client.put("/takeoff/aliases", payload)),

  // FRS (Final Room Schedule) native workbook import
  frsPreview: (id, file) => upload(`/estimates/${id}/frs/preview`, file),
  frsCreateEstimate: (file) => upload("/frs/create-estimate", file),
  getFrsScopeMap: () => unwrap(client.get("/frs/scope-map")),
  // EST-FRS-2
  getLaborLibrary: () => unwrap(client.get("/labor-library")),
  addLabor: (body) => unwrap(client.post("/labor-library", body)),
  updateLabor: (id, body) => unwrap(client.put(`/labor-library/${id}`, body)),
  getLaborLog: () => unwrap(client.get("/labor-library/log")),
  getLaborKeywords: () => unwrap(client.get("/labor-keywords")),
  saveLaborKeywords: (rows) => unwrap(client.put("/labor-keywords", { rows })),
  laborSuggest: (scope, descriptions) => unwrap(client.post("/labor-suggest", { scope, descriptions })),
  getSanityRules: () => unwrap(client.get("/sanity-rules")),
  saveSanityRules: (rules) => unwrap(client.put("/sanity-rules", { rules })),
  getSanity: (id) => unwrap(client.get(`/estimates/${id}/sanity`)),
  dismissSanity: (id, body) => unwrap(client.post(`/estimates/${id}/sanity/dismiss`, body)),
  libraryLookup: (params) => unwrap(client.get("/library/lookup", { params })),
  addToLibrary: (body) => unwrap(client.post("/library/add-from-line", body)),
  dismissLibraryPrompt: (lineId) => unwrap(client.post(`/lines/${lineId}/library-prompt-dismiss`)),
  listLibraryProposals: (status = "pending") => unwrap(client.get("/library/proposals", { params: { status } })),
  proposeLibraryUpdate: (body) => unwrap(client.post("/library/proposals", body)),
  decideLibraryProposal: (id, decision) => unwrap(client.post(`/library/proposals/${id}/${decision}`)),
  saveFrsScopeMap: (map) => unwrap(client.put("/frs/scope-map", map)),

  // Pipeline sync (Phase 10)
  getPipelineSettings: () => unwrap(client.get("/pipeline/status")),
  savePipelineSettings: (payload) => unwrap(client.put("/pipeline/status", payload)),
  getPipelineState: (id) => unwrap(client.get(`/estimates/${id}/pipeline`)),
  linkPipeline: (id, payload) => unwrap(client.post(`/estimates/${id}/pipeline/link`, payload)),
  unlinkPipeline: (id) => unwrap(client.post(`/estimates/${id}/pipeline/unlink`)),
  syncPipeline: (id) => unwrap(client.post(`/estimates/${id}/pipeline/sync`)),
  getPipelineLog: (id) => unwrap(client.get(`/estimates/${id}/pipeline/log`)),

  // Change orders (Phase 11)
  getWonProjects: () => unwrap(client.get("/won-projects")),
  getContractSummary: (id) => unwrap(client.get(`/estimates/${id}/contract-summary`)),
  listChangeOrders: (id) => unwrap(client.get(`/estimates/${id}/change-orders`)),
  createChangeOrder: (id, payload) => unwrap(client.post(`/estimates/${id}/change-orders`, payload)),
  getChangeOrder: (coId) => unwrap(client.get(`/change-orders/${coId}`)),
  updateChangeOrder: (coId, data) => unwrap(client.put(`/change-orders/${coId}`, { data })),
  setCoStatus: (coId, payload) => unwrap(client.post(`/change-orders/${coId}/status`, payload)),
  deleteChangeOrder: (coId) => unwrap(client.delete(`/change-orders/${coId}`)),
  copyCoLines: (coId, payload) => unwrap(client.post(`/change-orders/${coId}/copy-lines`, payload)),
  setCoLineSign: (coId, payload) => unwrap(client.post(`/change-orders/${coId}/lines/sign`, payload)),
  getAddsDeducts: (coId) => unwrap(client.get(`/change-orders/${coId}/adds-deducts`)),
};
